import React, { useState, useEffect } from 'react';
import { Container, Alert, Button, Spinner } from 'react-bootstrap';
import { 
  FaDatabase, FaChartBar, FaSearch, FaProjectDiagram, 
  FaExclamationTriangle, FaBrain, FaCogs, FaArrowLeft, 
  FaPlus, FaQuestionCircle, FaCube 
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as api from '../services/api';

// Components
import KnowledgeDashboard from '../components/knowledge/KnowledgeDashboard';
import KnowledgeExplorer from '../components/knowledge/KnowledgeExplorer';
import ClusterManager from '../components/knowledge/ClusterManager';
import ConflictResolution from '../components/knowledge/ConflictResolution';
import KnowledgeSources from '../components/knowledge/KnowledgeSources';
import ScoringConfiguration from '../components/knowledge/ScoringConfiguration';
import AddKnowledge from '../components/knowledge/AddKnowledge';
import QueryKnowledge from '../components/knowledge/QueryKnowledge';

import './KnowledgeBase.css';

const KnowledgeBase = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState('1');
  
  const [agentInfo, setAgentInfo] = useState({
    agent701: null,
    agent702: null,
    parentAgent: null
  });
  const [loadingAgentInfo, setLoadingAgentInfo] = useState(false);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    loadAgentInformation();
  }, []);

  const loadAgentInformation = async () => {
    try {
      setLoadingAgentInfo(true);
      
      const agentsResponse = await api.getAgents();
      let agentsList = [];
      if (agentsResponse?.body?.agents) agentsList = agentsResponse.body.agents;
      else if (Array.isArray(agentsResponse?.agents)) agentsList = agentsResponse.agents;
      else if (Array.isArray(agentsResponse?.data)) agentsList = agentsResponse.data;

      const parentAgent = agentsList.find(agent => 
        agent.repoName === 'AgentDeveloper' || 
        agent.agentName === 'AgentDeveloper' || 
        agent.uniqueIdentifier === '1'
      );

      const parentAgentName = parentAgent?.repoName || parentAgent?.agentName || 'AgentDeveloper';
      const microAgentsResponse = await api.getMicroAgents(parentAgentName);
      
      let microAgents = [];
      if (microAgentsResponse?.microAgents) microAgents = microAgentsResponse.microAgents;

      const agent701 = microAgents.find(a => String(a.agentNumber) === '701' || a.agentNumber === 701);
      const agent702 = microAgents.find(a => String(a.agentNumber) === '702' || a.agentNumber === 702);

      setAgentInfo({
        agent701,
        agent702,
        parentAgent: parentAgent || { uniqueIdentifier: '1', repoName: 'AgentDeveloper' }
      });

    } catch (error) {
      console.error('Error loading agent information:', error);
    } finally {
      setLoadingAgentInfo(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
    setSuccess(null);
  };

  const handleGoBack = () => {
    navigate('/');
  };

  const renderActiveTab = () => {
    const commonProps = { setError, setSuccess, selectedAgent, agentInfo };

    switch (activeTab) {
      case 'dashboard': return <KnowledgeDashboard {...commonProps} />;
      case 'add': return <AddKnowledge {...commonProps} />;
      case 'query': return <QueryKnowledge {...commonProps} />;
      case 'explorer': return <KnowledgeExplorer {...commonProps} />;
      case 'clusters': return <ClusterManager {...commonProps} />;
      case 'conflicts': return <ConflictResolution {...commonProps} />;
      case 'sources': return <KnowledgeSources {...commonProps} />;
      case 'settings': return <ScoringConfiguration {...commonProps} />;
      default: return <KnowledgeDashboard {...commonProps} />;
    }
  };

  return (
    <div className="knowledge-base-page fade-in">
      <Container fluid className="knowledge-container">
        
        <div className="knowledge-header slide-up">
          <div className="header-left">
            <FaCube className="title-icon" />
            <div className="page-title-group">
              <h1 className="page-title">Knowledge Matrix</h1>
              <p className="page-subtitle">Neural knowledge management & analysis</p>
            </div>
          </div>
          
          <div className="header-actions">
            <div className="agent-selector-wrapper">
              <span className="text-muted small text-uppercase fw-bold" style={{fontSize: '0.7rem'}}>Context:</span>
              <select 
                className="agent-select" 
                value={selectedAgent} 
                onChange={(e) => setSelectedAgent(e.target.value)}
              >
                <option value="all">All Agents</option>
                <option value="1">Agent 1 (Dev)</option>
                <option value="2">Agent 2 (API)</option>
              </select>
            </div>
            <Button 
              variant="outline-secondary" 
              onClick={handleGoBack}
              className="back-button"
            >
              <FaArrowLeft className="me-2" /> Hub
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="global-alert mb-3">
            <strong>Alert:</strong> {error}
          </Alert>
        )}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="global-alert mb-3">
            <strong>Success:</strong> {success}
          </Alert>
        )}

        <div className="kb-tabs-container slide-up" style={{'--index': 1}}>
          <div className="kb-horizontal-tabs">
            <div className="kb-tab-nav">
              <button className={`kb-tab-button ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => handleTabChange('dashboard')}>
                <FaChartBar className="tab-icon" /> Dashboard
              </button>
              <button className={`kb-tab-button ${activeTab === 'add' ? 'active' : ''}`} onClick={() => handleTabChange('add')}>
                <FaPlus className="tab-icon text-success" /> Add Data
              </button>
              <button className={`kb-tab-button ${activeTab === 'query' ? 'active' : ''}`} onClick={() => handleTabChange('query')}>
                <FaBrain className="tab-icon text-primary" /> Smart Search
              </button>
              <button className={`kb-tab-button ${activeTab === 'explorer' ? 'active' : ''}`} onClick={() => handleTabChange('explorer')}>
                <FaSearch className="tab-icon" /> Explorer
              </button>
              <button className={`kb-tab-button ${activeTab === 'clusters' ? 'active' : ''}`} onClick={() => handleTabChange('clusters')}>
                <FaProjectDiagram className="tab-icon" /> Clusters
              </button>
              <button className={`kb-tab-button ${activeTab === 'conflicts' ? 'active' : ''}`} onClick={() => handleTabChange('conflicts')}>
                <FaExclamationTriangle className="tab-icon" /> Conflicts
              </button>
              <button className={`kb-tab-button ${activeTab === 'sources' ? 'active' : ''}`} onClick={() => handleTabChange('sources')}>
                <FaDatabase className="tab-icon" /> Knowledge Sources
              </button>
              <button className={`kb-tab-button ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => handleTabChange('settings')}>
                <FaCogs className="tab-icon" /> Config
              </button>
            </div>
          </div>
        </div>

        <div className="kb-tab-content slide-up" style={{'--index': 2}}>
          {loadingAgentInfo && (activeTab === 'add') ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Syncing Neural Network Agents...</p>
            </div>
          ) : (
            renderActiveTab()
          )}
        </div>
      </Container>
    </div>
  );
};

export default KnowledgeBase;